import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createApiKey } from "@/lib/apiKeysService";

const RESOURCES = [
  { key: "spaces", label: "Spaces" },
  { key: "members", label: "Members" },
  { key: "requirements", label: "Requirements" },
  { key: "assignments", label: "Assignments" },
  { key: "compliance", label: "Compliance" },
  { key: "matrix", label: "Matrix" },
  { key: "qualifications", label: "Qualifications" },
  { key: "checkins", label: "Check-ins" },
  { key: "leave_requests", label: "Leave Requests" },
  { key: "files", label: "Files" },
  { key: "sms", label: "SMS" },
];

const DEFAULT_PERMISSIONS = {
  spaces: ["read"],
  members: ["read"],
  requirements: ["read"],
  assignments: ["read"],
  compliance: ["read"],
  matrix: ["read"],
  qualifications: ["read"],
  checkins: ["read"],
  leave_requests: ["read"],
  files: ["read"],
};

export function CreateApiKeyDialog({ open, onOpenChange, companyId, onCreated }) {
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [rateLimit, setRateLimit] = useState(60);
  const [creating, setCreating] = useState(false);

  const togglePermission = (resource, action) => {
    setPermissions((prev) => {
      const current = prev[resource] || [];
      const has = current.includes(action);
      let next;
      if (has) {
        next = current.filter((a) => a !== action);
      } else {
        next = [...current, action];
      }
      // Remove resource entirely if no permissions
      if (next.length === 0) {
        const { [resource]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [resource]: next };
    });
  };

  const hasPermission = (resource, action) => {
    return permissions[resource]?.includes(action) || false;
  };

  const selectAllRead = () => {
    const next = {};
    RESOURCES.forEach(({ key }) => {
      const existing = permissions[key] || [];
      const hasWrite = existing.includes("write");
      next[key] = hasWrite ? ["read", "write"] : ["read"];
    });
    setPermissions(next);
  };

  const selectAllWrite = () => {
    const next = {};
    RESOURCES.forEach(({ key }) => {
      next[key] = ["read", "write"];
    });
    setPermissions(next);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name for the API key");
      return;
    }

    if (Object.keys(permissions).length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    try {
      setCreating(true);
      const result = await createApiKey(companyId, {
        name: name.trim(),
        permissions,
        expiresAt: hasExpiry && expiresAt ? new Date(expiresAt).toISOString() : null,
        rateLimit,
      });

      onCreated(result);
      // Reset form
      setName("");
      setPermissions(DEFAULT_PERMISSIONS);
      setHasExpiry(false);
      setExpiresAt("");
      setRateLimit(60);
    } catch (err) {
      console.error("Failed to create API key:", err);
      toast.error(err.message || "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            Create a new API key with specific permissions for external access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="key-name">Key Name</Label>
            <Input
              id="key-name"
              placeholder="e.g. HR System Integration"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Permissions Grid */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Permissions</Label>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllRead}>
                  All Read
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllWrite}>
                  All R/W
                </Button>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-3 py-2 font-medium">Resource</th>
                    <th className="text-center px-3 py-2 font-medium w-16">Read</th>
                    <th className="text-center px-3 py-2 font-medium w-16">Write</th>
                  </tr>
                </thead>
                <tbody>
                  {RESOURCES.map(({ key, label }) => (
                    <tr key={key} className="border-b last:border-0">
                      <td className="px-3 py-2">{label}</td>
                      <td className="text-center px-3 py-2">
                        <Checkbox
                          checked={hasPermission(key, "read")}
                          onCheckedChange={() => togglePermission(key, "read")}
                        />
                      </td>
                      <td className="text-center px-3 py-2">
                        <Checkbox
                          checked={hasPermission(key, "write")}
                          onCheckedChange={() => togglePermission(key, "write")}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expiry */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="has-expiry">Set Expiry Date</Label>
              <Switch
                id="has-expiry"
                checked={hasExpiry}
                onCheckedChange={setHasExpiry}
              />
            </div>
            {hasExpiry && (
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            )}
          </div>

          {/* Rate Limit */}
          <div className="space-y-2">
            <Label htmlFor="rate-limit">Rate Limit (requests/minute)</Label>
            <Input
              id="rate-limit"
              type="number"
              min={1}
              max={1000}
              value={rateLimit}
              onChange={(e) => setRateLimit(parseInt(e.target.value) || 60)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Key"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
